import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { HeroSection } from '../components/sections/HeroSection';
import { RoomsPreview } from '../components/sections/RoomsPreview';
import { DiningPreview } from '../components/sections/DiningPreview';
import { ExplorePreview } from '../components/sections/ExplorePreview';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';
import { OffersPreview } from '../components/sections/OffersPreview';
import { InstagramFeed } from '../components/sections/InstagramFeed';
import { SEOHead, HotelSchema, LocalBusinessSchema, BookingActionSchema, OrganizationSchema, WebSiteSchema, PlaceSchema, AggregateRatingSchema } from '../components/SEO';
import { BookingComparisonTable, DirectBookingBenefits } from '../components/BookingIncentives';
import { motion } from 'framer-motion';

function BookDirectSection() {
  return (
    <section className="section-padding bg-emerald-900 text-white overflow-hidden">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 md:mb-10"
        >
          <p className="overline text-gold mb-3">Best Rate Guarantee</p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl mb-4">Book Direct & Save</h2>
          <p className="text-emerald-200 max-w-2xl mx-auto text-sm sm:text-base px-2">
            Enjoy exclusive benefits when you book directly with us. Lower rates, free breakfast, 
            and more perks you won't find on other booking sites.
          </p>
        </motion.div>

        {/* Stack on mobile, side by side on desktop */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch">
          {/* Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2"
          >
            <BookingComparisonTable className="h-full" />
          </motion.div>
          
          {/* Why Choose Direct Booking */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="w-full lg:w-1/2 space-y-4 sm:space-y-6"
          >
            <div className="bg-emerald-800/50 rounded-xl p-4 sm:p-6 border border-emerald-700">
              <h3 className="font-serif text-lg sm:text-xl mb-3 sm:mb-4">Why Choose Direct Booking?</h3>
              <ul className="space-y-2 sm:space-y-3 text-emerald-100 text-sm sm:text-base">
                <li className="flex items-start gap-2 sm:gap-3">
                  <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs sm:text-sm flex-shrink-0 mt-0.5">1</span>
                  <span><strong>Best Price Guaranteed</strong> – We match or beat any rate you find elsewhere</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs sm:text-sm flex-shrink-0 mt-0.5">2</span>
                  <span><strong>Free Breakfast</strong> – Start your day with our delicious breakfast buffet</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs sm:text-sm flex-shrink-0 mt-0.5">3</span>
                  <span><strong>Flexible Cancellation</strong> – Cancel free up to 24 hours before check-in</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs sm:text-sm flex-shrink-0 mt-0.5">4</span>
                  <span><strong>Personalized Service</strong> – Speak directly with our team for special requests</span>
                </li>
              </ul>
            </div>
            
            <a 
              href="/book" 
              className="btn-gold w-full flex items-center justify-center text-center py-3 sm:py-4 text-xs sm:text-sm"
              data-testid="homepage-book-direct-btn"
            >
              Book Direct Now – Best Rates Guaranteed
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEOHead 
        title="The Hotel Emerald | Luxury Hotel in Semiliguda, Odisha"
        description="Experience luxury hospitality at The Hotel Emerald, a premium hotel in the scenic Eastern Ghats of Semiliguda, Odisha. Book rooms from ₹2,499/night."
      />
      {/* Core Schema */}
      <HotelSchema />
      <LocalBusinessSchema />
      {/* Enhanced Voice/AI SEO Schema */}
      <OrganizationSchema />
      <WebSiteSchema />
      <PlaceSchema />
      <BookingActionSchema />
      <AggregateRatingSchema reviews={[]} />
      <Navbar />
      <main>
        <HeroSection />
        <RoomsPreview />
        <BookDirectSection />
        <DiningPreview />
        <ExplorePreview />
        <OffersPreview />
        <TestimonialsSection />
        <InstagramFeed />
      </main>
      <Footer />
    </div>
  );
}
